export interface LoggedUser {
  id: string;
  username: string;
  fullName: string;
  badgeNumber: string;
  role: 'DISPATCHER' | 'SUPERVISOR' | 'ADMIN';
  agencyId?: string;
  agencyName?: string;
  agencyCode?: string;
  token: string;
}

export class LoginModal {
  private backdrop: HTMLElement | null = null;
  private apiBaseUrl: string;
  private onLoginSuccess: (user: LoggedUser) => void;

  constructor(apiBaseUrl: string, onLoginSuccess: (user: LoggedUser) => void) {
    this.apiBaseUrl = apiBaseUrl;
    this.onLoginSuccess = onLoginSuccess;
  }

  public show(): void {
    if (this.backdrop) return;

    this.backdrop = document.createElement('div');
    this.backdrop.id = 'login-modal-backdrop';
    this.backdrop.style.cssText = `
      position: fixed;
      inset: 0;
      background: radial-gradient(circle at 50% 30%, rgba(15, 23, 42, 0.95), rgba(2, 6, 23, 0.98));
      backdrop-filter: blur(12px);
      z-index: 99999;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 16px;
      font-family: var(--font-sans, system-ui, sans-serif);
      color: #f8fafc;
    `;

    this.backdrop.innerHTML = `
      <div style="
        background: #0f172a;
      <div style="
        background: #0d1527;
        border: 1px solid rgba(81, 162, 218, 0.35);
        border-radius: 20px;
        width: 100%;
        max-width: 460px;
        box-shadow: 0 25px 60px -12px rgba(0, 0, 0, 0.85), 0 0 35px rgba(60, 110, 180, 0.2);
        overflow: hidden;
        animation: loginFadeIn 0.25s ease-out;
      ">
        <!-- Encabezado Institucional SOS Tech Ags -->
        <div style="background: linear-gradient(135deg, #10243e 0%, #0d1527 100%); padding: 26px 24px 20px; border-bottom: 1px solid rgba(81, 162, 218, 0.2); text-align: center;">
          <div style="margin-bottom: 14px; display: inline-flex; align-items: center; justify-content: center;">
            <svg viewBox="0 0 120 120" style="width: 58px; height: 58px; filter: drop-shadow(0 0 10px rgba(56, 189, 248, 0.5));" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="modalDiamondFrame" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stop-color="#38bdf8"/>
                  <stop offset="45%" stop-color="#0284c7"/>
                  <stop offset="100%" stop-color="#0f172a"/>
                </linearGradient>
                <linearGradient id="modalBevel" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stop-color="#ffffff" stop-opacity="0.8"/>
                  <stop offset="100%" stop-color="#38bdf8" stop-opacity="0.2"/>
                </linearGradient>
                <linearGradient id="modalSGlyph" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stop-color="#ffffff"/>
                  <stop offset="35%" stop-color="#7dd3fc"/>
                  <stop offset="80%" stop-color="#0284c7"/>
                  <stop offset="100%" stop-color="#0369a1"/>
                </linearGradient>
              </defs>
              <polygon points="36,14 84,14 110,38 60,112 10,38" fill="#090d16" stroke="url(#modalDiamondFrame)" stroke-width="4.5" stroke-linejoin="round"/>
              <polygon points="36,14 84,14 74,24 46,24" fill="url(#modalBevel)" opacity="0.8"/>
              <polygon points="10,38 36,14 46,24 22,42" fill="#1e293b" opacity="0.9"/>
              <polygon points="110,38 84,14 74,24 98,42" fill="#0f172a" opacity="0.9"/>
              <polygon points="22,42 98,42 60,102" fill="#050a14" stroke="#0284c7" stroke-width="1.2" stroke-opacity="0.6"/>
              <path d="M 28 32 L 36 32 L 44 40" stroke="#38bdf8" stroke-width="1.6" stroke-linecap="round" fill="none" opacity="0.85"/>
              <circle cx="28" cy="32" r="2" fill="#67e8f9"/>
              <path d="M 92 32 L 84 32 L 76 40" stroke="#38bdf8" stroke-width="1.6" stroke-linecap="round" fill="none" opacity="0.85"/>
              <circle cx="92" cy="32" r="2" fill="#67e8f9"/>
              <path d="M 76 36 L 46 36 L 40 54 L 68 54 L 62 76 L 38 76 L 36 84 L 74 84 L 80 64 L 52 64 L 58 44 L 78 44 Z" fill="url(#modalSGlyph)" stroke="#38bdf8" stroke-width="1.8" stroke-linejoin="miter"/>
              <path d="M 50 94 L 60 106 L 70 94" fill="none" stroke="#38bdf8" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <div style="font-size: 0.72rem; font-weight: 800; letter-spacing: 0.12em; color: #51a2da; text-transform: uppercase; margin-bottom: 4px; font-family: var(--font-mono, monospace);">
            SOS TECH [AGS] • MISIÓN CRÍTICA
          </div>
          <h2 style="margin: 0; font-size: 1.45rem; font-weight: 800; letter-spacing: -0.5px; color: #fff;">
            SOS VideoCall™
          </h2>
          <div style="margin: 4px 0 0; font-size: 0.85rem; font-weight: 700; color: #38bdf8;">
            Tele-Triage &amp; Videollamada 911 • Centro de Comando
          </div>
          <p style="margin: 8px auto 0; font-size: 0.78rem; color: #94a3b8; line-height: 1.45; max-width: 380px;">
            Videollamada de emergencia sin app (&lt;500ms) y geolocalización satelital instantánea para centros de comando, paramédicos en ruta y ciudadanos en escena.
          </p>
        </div>

        <!-- Formulario -->
        <form id="form-sos-login" style="padding: 24px; display: flex; flex-direction: column; gap: 16px;">
          <div id="login-error-msg" style="display: none; background: rgba(239, 68, 68, 0.15); border: 1px solid #ef4444; border-radius: 8px; padding: 10px 14px; font-size: 0.82rem; color: #fca5a5;"></div>

          <div style="display: flex; flex-direction: column; gap: 6px;">
            <label style="font-size: 0.78rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: #cbd5e1;">
              Usuario Operativo
            </label>
            <div style="position: relative;">
              <span style="position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: #64748b;">👤</span>
              <input type="text" id="login-username" required autocomplete="username" placeholder="ej. paramedico, supervisor, admin" style="
                width: 100%;
                background: #111e38;
                border: 1px solid rgba(81, 162, 218, 0.25);
                border-radius: 8px;
                padding: 11px 12px 11px 38px;
                color: #fff;
                font-family: inherit;
                font-size: 0.95rem;
                outline: none;
                box-sizing: border-box;
                transition: border-color 0.2s, box-shadow 0.2s;
              " />
            </div>
          </div>

          <div style="display: flex; flex-direction: column; gap: 6px;">
            <label style="font-size: 0.78rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: #cbd5e1;">
              Contraseña
            </label>
            <div style="position: relative;">
              <span style="position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: #64748b;">🔒</span>
              <input type="password" id="login-password" required autocomplete="current-password" placeholder="••••••••" style="
                width: 100%;
                background: #111e38;
                border: 1px solid rgba(81, 162, 218, 0.25);
                border-radius: 8px;
                padding: 11px 12px 11px 38px;
                color: #fff;
                font-family: inherit;
                font-size: 0.95rem;
                outline: none;
                box-sizing: border-box;
                transition: border-color 0.2s, box-shadow 0.2s;
              " />
            </div>
          </div>

          <!-- Botón de Envío Fedora Pro -->
          <button type="submit" id="btn-submit-login" style="
            background: linear-gradient(135deg, #1e3762 0%, #3c6eb4 100%);
            color: #fff;
            border: 1px solid rgba(81, 162, 218, 0.4);
            border-radius: 10px;
            padding: 13px;
            font-size: 0.95rem;
            font-weight: 700;
            cursor: pointer;
            box-shadow: 0 4px 16px rgba(60, 110, 180, 0.35);
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            transition: all 0.2s ease;
            margin-top: 4px;
          ">
            <span>INGRESAR A LA PLATAFORMA</span>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="9 18 15 12 9 6"></polyline></svg>
          </button>
        </form>

        <div style="background: rgba(0,0,0,0.35); padding: 12px 24px; text-align: center; border-top: 1px solid rgba(255,255,255,0.06);">
          <div style="font-size: 0.72rem; color: #94a3b8; font-weight: 600; margin-bottom: 2px;">
            SOS Tech Ags &bull; Aguascalientes, México &bull; <a href="https://sostechags.xyz" target="_blank" style="color:#51a2da; text-decoration:none;">sostechags.xyz</a>
          </div>
          <span style="font-size: 0.67rem; color: #64748b; letter-spacing: 0.04em; font-family: var(--font-mono, monospace);">
            ESTÁNDARES NG911 / HIPAA / NOM-024-SSA3-2012 &bull; AUDITORÍA FORENSE ACTIVA
          </span>
        </div>
      </div>
    `;

    document.body.appendChild(this.backdrop);
    this.setupListeners();
  }

  public hide(): void {
    if (this.backdrop) {
      this.backdrop.remove();
      this.backdrop = null;
    }
  }

  private setupListeners(): void {
    if (!this.backdrop) return;

    const form = this.backdrop.querySelector('#form-sos-login') as HTMLFormElement;
    const userInput = this.backdrop.querySelector('#login-username') as HTMLInputElement;
    const passInput = this.backdrop.querySelector('#login-password') as HTMLInputElement;
    const errorBox = this.backdrop.querySelector('#login-error-msg') as HTMLElement;
    const submitBtn = this.backdrop.querySelector('#btn-submit-login') as HTMLButtonElement;

    // Enfoque inicial
    userInput?.focus();

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      errorBox.style.display = 'none';
      submitBtn.disabled = true;
      submitBtn.innerHTML = 'Verificando credenciales...';

      try {
        const res = await fetch(`${this.apiBaseUrl}/api/v1/auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            username: userInput.value.trim(),
            password: passInput.value
          })
        });

        const data = await res.json();

        if (!res.ok || !data.success) {
          throw new Error(data.message || data.error || 'Credenciales no válidas');
        }

        const user: LoggedUser = data.data;
        this.hide();
        this.onLoginSuccess(user);
      } catch (err: any) {
        errorBox.textContent = `❌ ${err.message || 'Error al iniciar sesión'}`;
        errorBox.style.display = 'block';
        submitBtn.disabled = false;
        submitBtn.innerHTML = `<span>INGRESAR A LA PLATAFORMA</span><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="9 18 15 12 9 6"></polyline></svg>`;
      }
    });
  }
}
